import axios from 'axios';

/**
 * Triggers a real browser download so the browser's own download manager
 * handles the transfer. This gives the exact behaviour users expect from any
 * normal website download:
 *   - native download progress in the browser's download bar / manager,
 *   - the file streams straight to disk (no buffering the whole file in the
 *     page's memory, so large files work reliably),
 *   - pause / resume / retry / cancel support from the browser UI.
 *
 * Flow:
 *  1. Ask the API for a short-lived, single-file download token. This AJAX
 *     call uses the normal JWT "Authorization" header.
 *  2. Navigate the browser to the download URL with "?token=...". The server
 *     responds with "Content-Disposition: attachment", so the page does not
 *     navigate away and the browser takes over the transfer with its usual
 *     download UI.
 */
export async function triggerBrowserDownload({ type, filename, userId = null, headers = {} }) {
  const params = { type, filename };
  if (userId) {
    params.user_id = userId;
  }

  // 1. Get a short-lived download URL (token is bound to this one file).
  const res = await axios.get('/drive/api/download-token', { params, headers });
  const url = res.data && res.data.download_url;
  if (!url) {
    throw new Error('Server did not return a download URL');
  }

  // 2. Trigger the download as a real browser navigation.
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
